import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { AuthQuery } from '@shared/store/auth/auth.query';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { MainQuery } from '@shared/store/main/main.query';
import { userFormatter } from '@shared/utils/user-formatter';

import { InvoiceFormComponent } from './invoice-form.component';
import { InvoiceDetailApproveBtnComponent } from './status-banner-buttons/invoice-detail-banner-approve-button.component';
import { InvoiceDetailDeclineBtnComponent } from './status-banner-buttons/invoice-detail-banner-decline-button.component';
import { InvoiceDetailDeleteBtnComponent } from './status-banner-buttons/invoice-detail-banner-delete-button.component';
import { InvoiceDetailDownloadBtnComponent } from './status-banner-buttons/invoice-detail-banner-download-button.component';
import { InvoiceDetailSaveBtnComponent } from './status-banner-buttons/invoice-detail-banner-save-button.component';

dayjs.extend(localizedFormat);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
  },
  imports: [
    InvoiceDetailApproveBtnComponent,
    InvoiceDetailSaveBtnComponent,
    InvoiceDetailDeleteBtnComponent,
    InvoiceDetailDeclineBtnComponent,
    InvoiceDetailDownloadBtnComponent,
  ],
  selector: 'aux-invoice-detail-status-banner',
  styles: [
    `
      :host {
        @apply mb-4 block border px-4;
      }
    `,
  ],
  template: `
    <div class="flex h-16 items-center justify-between">
      <div>
        <span>Status:</span>
        <span class="ml-2 font-bold">{{ status() }}</span>
        @if ((isApproved() || isDecline()) && approvedBy()) {
          <span>{{ approvedBy() }}</span>
        }
      </div>

      <div class="flex items-center space-x-2">
        <aux-invoice-detail-status-banner-save-button
          [invoice]="invoice()"
          [invoiceFormComponent]="invoiceFormComponent()"
        />

        <aux-invoice-detail-status-banner-download-button [invoice]="invoice()" />

        @if (isPendingReview() || isInQueue()) {
          <aux-invoice-detail-status-banner-delete-button [invoice]="invoice()" />
        }

        @if (isPendingApproval()) {
          <aux-invoice-detail-status-banner-decline-button [invoice]="invoice()" />
          <aux-invoice-detail-status-banner-approve-button [invoice]="invoice()" />
          <aux-invoice-detail-status-banner-delete-button [invoice]="invoice()" />
        }
      </div>
    </div>

    @if (isDecline()) {
      <div class="mt-4 bg-aux-gray-light p-2 text-aux-black">
        <div class="font-bold">Reason for Decline</div>
        @if (declineReason()) {
          <div>{{ declineReason() }}</div>
        }
      </div>
    }
  `,
})
export class InvoiceDetailStatusBannerComponent {
  authQuery = inject(AuthQuery);
  mainQuery = inject(MainQuery);

  invoice = input.required<InvoiceModel>();

  approvedBy = computed(() => {
    const invoice = this.invoice();
    const userMap = this.mainQuery.userMap();
    const adminUser = this.authQuery.adminUser();

    const approval = invoice.approvals.at(-1);

    if (!approval) {
      return '';
    }

    const approvedBy = userFormatter({
      adminUser,
      sub: approval.aux_user_id,
      userMap,
    });

    return `by ${approvedBy} on ${dayjs(approval.approval_time || '').format('LL')}`;
  });
  declineReason = computed(() => this.invoice().decline_reason);
  hostClasses = computed(() => {
    const classList: string[] = [];

    if (this.isPendingReview() || this.isInQueue() || this.isPendingApproval()) {
      classList.push('bg-aux-blue-light-50', 'text-aux-black');
    }

    if (this.isApproved()) {
      classList.push('bg-aux-green-dark', 'text-white');
    }

    if (this.isDecline()) {
      classList.push('bg-aux-red-dark', 'pb-4', 'text-white');
    }

    return classList;
  });
  isApproved = computed(() => this.invoice().invoice_status === 'STATUS_APPROVED');
  isDecline = computed(() => this.invoice().invoice_status === 'STATUS_DECLINED');
  isInQueue = computed(() => this.invoice().invoice_status === 'STATUS_IN_QUEUE');
  isPendingApproval = computed(() => this.invoice().invoice_status === 'STATUS_PENDING_APPROVAL');
  isPendingReview = computed(() => this.invoice().invoice_status === 'STATUS_PENDING_REVIEW');
  status = computed(() => {
    const invoice = this.invoice();

    switch (invoice.invoice_status) {
      case 'STATUS_APPROVED':
        return 'Approved';
      case 'STATUS_DECLINED':
        return 'Declined';
      case 'STATUS_IN_QUEUE':
        return 'In Queue';
      case 'STATUS_PENDING_APPROVAL':
        return 'Pending Approval';
      case 'STATUS_PENDING_REVIEW':
        return 'Pending Review';
      default:
        return '';
    }
  });

  invoiceFormComponent = input.required<InvoiceFormComponent>();
}
